import { useEffect, useState } from 'react';
import { Clock, Loader2 } from 'lucide-react';
import { INGEST_INTERVALS, ingestWindowSlots, nextIngestWindow, validateIngestWindow } from '@repo/shared-types';
import { api, type IngestSettings } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input, Select } from '../ui/input';

/**
 * When this project absorbs what its automatic sources send it.
 *
 * On the project's Integrations tab rather than in a tab of its own, because it is the *when* of the
 * sources listed beside it: a channel wired above and a window set here are one decision about how
 * this project takes in material, and separating them would mean reading two pages to know what
 * happens to a message.
 *
 * The reason to hold arrivals is the model. A busy channel produces dozens of small messages, and a run
 * each has a flagship model re-reading the whole tree per line of chat — worse, it never sees two
 * related messages together, so the second arrives as an unexplained contradiction of the page the
 * first one wrote.
 *
 * One "Absorb" choice covers all three shapes — on arrival, every N minutes, once a day — because to a
 * person they are one question (how long may material wait?), even though they are stored as a mode,
 * an interval and a time.
 */

/** The Absorb select's value: the mode and the interval folded into one choice. */
type Cadence = 'IMMEDIATE' | 'DAILY' | `${number}`;

function cadenceOf(settings: IngestSettings): Cadence {
  if (settings.ingestMode === 'IMMEDIATE') return 'IMMEDIATE';
  return settings.ingestEveryMinutes ? (String(settings.ingestEveryMinutes) as Cadence) : 'DAILY';
}
export function IngestWindowCard({ projectId, projectName }: { projectId: string; projectName: string }) {
  const [settings, setSettings] = useState<IngestSettings | null>(null);
  const [draft, setDraft] = useState<IngestSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    // Read from the org-level queue view rather than a per-project endpoint: it already returns every
    // project's window, and one fetch is what the Waiting tab does with the same numbers.
    api.kb
      .ingestQueue()
      .then((page) => {
        const mine = page.projects.find((row) => row.projectId === projectId);
        const current: IngestSettings = {
          ingestMode: mine?.mode ?? 'IMMEDIATE',
          ingestTimeOfDay: mine?.timeOfDay ?? null,
          ingestTimezone: mine?.timezone ?? guessTimezone(),
          ingestEveryMinutes: mine?.everyMinutes ?? null,
          ingestBatchMaxChars: mine?.batchMaxChars ?? null,
        };
        setSettings(current);
        setDraft(current);
      })
      .catch(() => setError('Could not read this project’s ingestion window'));
  }, [projectId]);

  if (!draft || !settings) {
    return (
      <Card>
        <CardHeader className="flex items-center gap-2">
          <Clock size={15} className="text-ink-faint" />
          <CardTitle>When arrivals are absorbed</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="flex items-center gap-2 text-xs text-ink-faint py-2">
            {error ?? (
              <>
                <Loader2 size={13} className="animate-spin" />
                Loading…
              </>
            )}
          </p>
        </CardContent>
      </Card>
    );
  }

  const scheduled = draft.ingestMode === 'SCHEDULED';
  const cadence = cadenceOf(draft);
  const interval = scheduled && draft.ingestEveryMinutes != null;
  const windowSpec = { timeOfDay: draft.ingestTimeOfDay, timezone: draft.ingestTimezone, everyMinutes: draft.ingestEveryMinutes };
  // The same validation the API applies, so Save is disabled for exactly the windows it would refuse.
  const problem = scheduled ? validateIngestWindow(windowSpec) : null;
  const dirty = JSON.stringify(draft) !== JSON.stringify(settings);
  // From the module the worker's tick uses, so the preview is the instant it will actually fire.
  const preview = scheduled && !problem ? nextIngestWindow(windowSpec) : null;
  const slots = interval && !problem ? ingestWindowSlots(windowSpec) : [];

  const set = <K extends keyof IngestSettings>(key: K, value: IngestSettings[K]) =>
    setDraft((current) => (current ? { ...current, [key]: value } : current));

  const setCadence = (value: Cadence) =>
    setDraft((current) => {
      if (!current) return current;
      if (value === 'IMMEDIATE') return { ...current, ingestMode: 'IMMEDIATE' };
      return {
        ...current,
        ingestMode: 'SCHEDULED',
        ingestEveryMinutes: value === 'DAILY' ? null : Number(value),
        // A window with no time never fires, so choosing a schedule gives it one to start from.
        ingestTimeOfDay: current.ingestTimeOfDay ?? '00:00',
      };
    });

  const save = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const updated = await api.kb.updateIngestSettings(projectId, draft);
      setSettings(updated);
      setDraft(updated);
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader className="flex items-center gap-2">
        <Clock size={15} className={scheduled ? 'text-orange-500' : 'text-ink-faint'} />
        <CardTitle>When arrivals are absorbed</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-ink-muted leading-relaxed">
          Applies to the automatic sources above, and to anything routed here from a global source. A
          document you paste, a file you upload and a note an automation saves are always absorbed at
          once — someone is waiting for the run.
        </p>

        {error && (
          <p className="text-xs text-danger-text bg-danger-bg border border-danger-ring rounded px-3 py-2">{error}</p>
        )}
        {saved && !dirty && (
          <p className="text-xs text-success-text bg-success-bg border border-success-ring rounded px-3 py-2">
            Saved. The next window is the first one to fire — changing the time never fires it retroactively.
          </p>
        )}

        <div className="space-y-1">
          <label className="text-xs font-medium text-ink-secondary">Absorb</label>
          <Select value={cadence} onChange={(e) => setCadence(e.target.value as Cadence)}>
            <option value="IMMEDIATE">As each item arrives</option>
            {INGEST_INTERVALS.map((option) => (
              <option key={option.minutes} value={String(option.minutes)}>
                {option.label}, together
              </option>
            ))}
            <option value="DAILY">Once a day, all together</option>
          </Select>
        </div>

        {scheduled && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-xs font-medium text-ink-secondary">{interval ? 'Starting at' : 'At'}</label>
                <Input
                  type="time"
                  value={draft.ingestTimeOfDay ?? ''}
                  onChange={(e) => set('ingestTimeOfDay', e.target.value || null)}
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-ink-secondary">Timezone</label>
                <Input
                  value={draft.ingestTimezone}
                  onChange={(e) => set('ingestTimezone', e.target.value)}
                  placeholder="Europe/London"
                  className="font-mono text-xs"
                />
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-medium text-ink-secondary">Batch size limit</label>
              <Input
                type="number"
                value={draft.ingestBatchMaxChars ?? ''}
                onChange={(e) => set('ingestBatchMaxChars', e.target.value ? Number(e.target.value) : null)}
                placeholder="Server default"
              />
              <p className="text-[11px] text-ink-faint leading-snug">
                Characters one run may take on. Whole items are packed up to this, and an item bigger than
                it is absorbed on its own rather than cut in half — so a batch never splits a message or a
                meeting note down the middle. Blank uses the server's default.
              </p>
            </div>

            {slots.length > 1 && (
              <p className="text-[11px] text-ink-faint leading-snug">
                Opens {slots.length} times a day: {slots.slice(0, 4).join(', ')}
                {slots.length > 4 ? ` … ${slots[slots.length - 1]}` : ''}. Shorter windows get material into the
                pages sooner, but each run reads less at a time, so the same material costs more to absorb.
              </p>
            )}

            <p className="text-[11px] text-ink-faint">
              {problem ? (
                <span className="text-warning-text">{problem}</span>
              ) : preview ? (
                <>Next window: {preview.toLocaleString()}</>
              ) : (
                <>Set a time for this window to fire.</>
              )}
            </p>
          </>
        )}

        {!scheduled && (
          <p className="text-[11px] text-ink-faint">
            {projectName} absorbs each arrival as it lands — one run per message or note.
          </p>
        )}

        <Button onClick={save} disabled={!dirty || saving || problem !== null}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </CardContent>
    </Card>
  );
}

/** The browser's own zone as the default for a project that has never set one — better than UTC. */
function guessTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}
